import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../../../context/CartContext';
import { useWishlist } from '../../../context/WishlistContext';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Rating,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Breadcrumbs,
  Link,
  Skeleton
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  FavoriteBorder,
  Share,
  LocalShipping,
  CheckCircle,
  KeyboardArrowRight,
  PlayCircleFilled,
  Close
} from '@mui/icons-material';
import { Modal, Backdrop, Fade, IconButton as MuiIconButton } from '@mui/material';
import videoSource from '../../../assets/video1.mp4';
import SEO from '../../common/SEO';
import { apiList, invokeGetApi } from '../../../services/apiServices';
import { config } from '../../../config/config';
import ProductCard from '../../common/ProductCard';
import { products as localProducts } from '../../../config/products';

import { getProductImage } from '../../../utils/imageHelper';

const getImageSrc = (image) => {
  return getProductImage(image);
};

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toggleWishlist, isWishlisted } = useWishlist();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [addedRecently, setAddedRecently] = useState(false);

  const currentPrice = selectedVariant ? selectedVariant.price : (product?.price || 0);
  const currentOldPrice = selectedVariant?.originalPrice || product?.oldPrice || 0;
  const currentSize = selectedVariant ? selectedVariant.size : (product?.weight || '1 PC');

  const handleAddToCart = () => {
    const mainImg = product?.image || (product?.images && product?.images[0]) || '';
    addToCart({ ...product, price: currentPrice, image: mainImg }, quantity, currentSize);
    setAddedRecently(true);
    setTimeout(() => setAddedRecently(false), 2500);
  };

  const handleBuyNow = () => {
    const mainImg = product?.image || (product?.images && product?.images[0]) || '';
    addToCart({ ...product, price: currentPrice, image: mainImg }, quantity, currentSize, { silent: true });
    navigate('/checkout');
  };

  useEffect(() => {
    const handleScroll = () => setShowStickyBar(window.scrollY > 500);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await invokeGetApi("/" + slug);
        const data = response.data;
        if (data.variants && typeof data.variants === 'string') {
          data.variants = JSON.parse(data.variants);
        }
        if (data.images && typeof data.images === 'string') {
          try {
            data.images = JSON.parse(data.images);
          } catch (e) {
            data.images = [data.images];
          }
        }
        if (!data.images || (Array.isArray(data.images) && data.images.length === 0)) {
            data.images = [data.image];
        }
        setProduct(data);
        if (data.variants && data.variants.length > 0) {
          setSelectedVariant(data.variants[0]);
        }
        // ── Recently Viewed (persist last 6 slugs in localStorage) ──
        const stored = JSON.parse(localStorage.getItem('ar_recently_viewed') || '[]');
        const updated = [slug, ...stored.filter(s => s !== slug)].slice(0, 6);
        localStorage.setItem('ar_recently_viewed', JSON.stringify(updated));
        const viewed = localProducts.filter(p => updated.includes(p.slug) && p.slug !== slug).slice(0, 4);
        setRecentlyViewed(viewed);
      } catch (error) {
        console.error("Error fetching product details:", error);
        // Fallback to local products
        const local = localProducts.find(p => p.slug === slug);
        if (local) {
          setProduct(local);
          const stored = JSON.parse(localStorage.getItem('ar_recently_viewed') || '[]');
          const updated = [slug, ...stored.filter(s => s !== slug)].slice(0, 6);
          localStorage.setItem('ar_recently_viewed', JSON.stringify(updated));
          const viewed = localProducts.filter(p => updated.includes(p.slug) && p.slug !== slug).slice(0, 4);
          setRecentlyViewed(viewed);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug]);

  const getMappedImage = (imgKey) => {
    return getImageSrc(imgKey);
  };

  if (loading) return (
    <Container sx={{ py: 10 }}>
      <Grid container spacing={6}>
        <Grid item xs={12} md={6}><Skeleton variant="rectangular" height={500} /></Grid>
        <Grid item xs={12} md={6}><Skeleton variant="text" height={60} /><Skeleton variant="text" width="60%" /></Grid>
      </Grid>
    </Container>
  );

  if (!product) return <Typography>Product not found.</Typography>;

  return (
    <Box sx={{ py: 6 }}>
      {/* Sticky Buy Now Bar */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          bgcolor: '#fff',
          borderTop: '1px solid #eee',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
          px: { xs: 2, md: 4 },
          py: 1.5,
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          transition: 'transform 0.3s ease',
          transform: showStickyBar ? 'translateY(0)' : 'translateY(100%)',
        }}
      >
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {product?.name}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#1B1F23', lineHeight: 1 }}>
            ₹{currentPrice}
            {currentOldPrice > 0 && Number(currentOldPrice) > Number(currentPrice) && (
              <Typography component="span" variant="caption" sx={{ textDecoration: 'line-through', color: '#999', ml: 1 }}>
                ₹{currentOldPrice}
              </Typography>
            )}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          onClick={handleAddToCart}
          sx={{
            borderRadius: 1,
            px: 3,
            py: 1.2,
            fontWeight: 700,
            flexShrink: 0,
            bgcolor: addedRecently ? '#10B981' : '#0E6ADA',
            '&:hover': {
              bgcolor: addedRecently ? '#059669' : '#0c57b3'
            }
          }}
        >
          {addedRecently ? '✓ ADDED' : 'ADD TO CART'}
        </Button>
      </Box>
      <SEO
        title={product.name}
        description={`${product.name} - ₹${product.price}. Genuine software license with instant digital delivery.`}
        ogImage={getMappedImage(product.images?.[0])}
        product={{
          name: product.name,
          price: product.price,
          currency: 'INR',
          inStock: product.stockStatus !== 'Out of Stock',
        }}
      />
      <Container maxWidth="lg">
        <Breadcrumbs sx={{ mb: 4 }}>
          <Link underline="hover" color="inherit" href="/">Home</Link>
          <Link underline="hover" color="inherit" href="/shop">Shop</Link>
          <Typography color="text.primary">{product.name}</Typography>
        </Breadcrumbs>

        <Grid container spacing={8}>
          {/* Left: Image Gallery */}
          <Grid item xs={12} md={6}>
            <Box sx={{ position: 'sticky', top: 100 }}>
              <Box sx={{ position: 'relative' }}>
                <Box
                  component="img"
                  src={getMappedImage(product.images?.[selectedImage] || product.image)}
                  sx={{ width: '100%', height: 'auto', backgroundColor: '#F5F5F5', mb: 2 }}
                />
              </Box>

              <Grid container spacing={2}>
                {product.images?.map((imgKey, idx) => (
                  <Grid item xs={3} key={idx}>
                    <Box
                      component="img"
                      src={getMappedImage(imgKey)}
                      onClick={() => setSelectedImage(idx)}
                      sx={{
                        width: '100%',
                        cursor: 'pointer',
                        border: selectedImage === idx ? '2px solid' : '1px solid #EEE',
                        borderColor: selectedImage === idx ? 'primary.main' : '#EEE'
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>

          {/* Right: Product Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>{product.name}</Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Rating value={product.rating} precision={0.1} readOnly />
              <Typography variant="body2" color="text.secondary">({product.reviewsCount} reviews)</Typography>
              <Divider orientation="vertical" flexItem />
              <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>{product.stockStatus}</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
              <Typography variant="h3" color="primary.main" sx={{ fontWeight: 700 }}>₹{currentPrice}</Typography>
              {/* GST Badge */}
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                bgcolor: '#EEF2FF',
                border: '1px solid #C7D2FE',
                borderRadius: '6px',
                px: 1,
                py: 0.4,
              }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#4338CA', letterSpacing: 0.3 }}>
                  +18% GST
                </Typography>
              </Box>
              {currentOldPrice > 0 && Number(currentOldPrice) > Number(currentPrice) && (
                <Typography variant="h5" color="text.secondary" sx={{ textDecoration: 'line-through' }}>₹{currentOldPrice}</Typography>
              )}
            </Box>

            {/* Instant Delivery Alert */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4, bgcolor: 'rgba(14, 106, 218, 0.05)', p: 1.5, border: '1px dashed #0e6ada' }}>
              <Box component="span" sx={{ fontSize: '1.2rem' }}>⚡</Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Instant Delivery: Product key will be sent to your email address immediately.
              </Typography>
            </Box>

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>LICENSE TYPE</Typography>
            <ToggleButtonGroup
              value={selectedVariant?.size || currentSize}
              exclusive
              onChange={(e, nextSize) => {
                const variant = product.variants?.find(v => v.size === nextSize);
                if (variant) setSelectedVariant(variant);
              }}
              sx={{ mb: 4 }}
            >
              {product.variants && Array.isArray(product.variants) && product.variants.length > 0 ? (
                product.variants.map((v) => (
                  <ToggleButton key={v.size} value={v.size} sx={{ px: 3 }}>{v.size}</ToggleButton>
                ))
              ) : (
                <ToggleButton value={product.weight || 'Retail Key'} sx={{ px: 3 }}>{product.weight || 'Retail Key'}</ToggleButton>
              )}
            </ToggleButtonGroup>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #DDD' }}>
                <IconButton onClick={() => setQuantity(Math.max(1, quantity - 1))}><RemoveIcon /></IconButton>
                <Typography sx={{ px: 3 }}>{quantity}</Typography>
                <IconButton onClick={() => setQuantity(quantity + 1)}><AddIcon /></IconButton>
              </Box>
              <Button
                variant="contained"
                size="large"
                sx={{
                  flexGrow: 1,
                  borderRadius: 1,
                  fontWeight: 700,
                  bgcolor: addedRecently ? '#10B981' : '#0E6ADA',
                  '&:hover': {
                    bgcolor: addedRecently ? '#059669' : '#0c57b3'
                  }
                }}
                onClick={handleAddToCart}
              >
                {addedRecently ? '✓ ADDED TO CART' : 'ADD TO CART'}
              </Button>
              <IconButton 
                onClick={() => toggleWishlist(product)}
                sx={{ 
                  border: '1px solid #DDD',
                  color: isWishlisted(product.id) ? 'error.main' : 'inherit'
                }}
              >
                <FavoriteBorder sx={{ fill: isWishlisted(product.id) ? 'currentColor' : 'none' }} />
              </IconButton>
            </Box>
            {/* Buy Now */}
            <Button
              variant="outlined"
              color="primary"
              size="large"
              fullWidth
              onClick={handleBuyNow}
              sx={{ mb: 6, fontWeight: 700, borderRadius: 1, textTransform: 'uppercase', letterSpacing: 1 }}
            >
              ⚡ Buy Now
            </Button>

            <Divider sx={{ mb: 4 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <CheckCircle sx={{ color: 'primary.main' }} />
                <Typography variant="body2">100% Genuine product activation key</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <CheckCircle sx={{ color: 'primary.main' }} />
                <Typography variant="body2">Receives official Microsoft updates and security patches</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Product Details Tabs */}
        <Box sx={{ mt: 10 }}>
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}
          >
            <Tab label="DESCRIPTION" />
            <Tab label="NUTRITION & BENEFITS" />
          </Tabs>

          <Box sx={{ minHeight: 200 }}>
            {tabValue === 0 && (
              <Typography variant="body1" sx={{ lineHeight: 1.8, maxWidth: '800px' }}>
                {product.description || "Detailed description coming soon..."}
              </Typography>
            )}

            {tabValue === 1 && (
              <Grid container spacing={6}>
                <Grid item xs={12} md={6}>
                  <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #EEE' }}>
                    <Table size="small">
                      <TableBody>
                        {product.specs?.map((row) => (
                          <TableRow key={row.label}>
                            <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                            <TableCell align="right">{row.value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>KEY FEATURES & BENEFITS</Typography>
                  {product.features?.map((b, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <KeyboardArrowRight color="primary" />
                      <Typography variant="body2">{b}</Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            )}
          </Box>
        </Box>

        {/* Comparison Section Enhancement */}
        <Box sx={{ mt: 15 }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 800 }}>
            Choose the Right Microsoft License
          </Typography>
          <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 6 }}>
            Understand the difference between Retail and OEM keys before purchasing.
          </Typography>

          <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: '0 10px 40px rgba(0,0,0,0.05)' }}>
            <Table>
              <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>FEATURE</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: 'primary.main' }}>RETAIL LICENSE KEY</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>OEM LICENSE KEY</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  { feature: 'Transferability', retail: 'Yes (Can be moved to a new PC)', oem: 'No (Locked to first motherboard activated)' },
                  { feature: 'Microsoft Support', retail: 'Full support included from Microsoft', oem: 'Self-support / Vendor support only' },
                  { feature: 'Account Link', retail: 'Links to personal Microsoft Account', oem: 'Binds to hardware ID' },
                  { feature: 'Re-activation', retail: 'Multiple times (on one PC at a time)', oem: 'Lifetime on same motherboard' },
                  { feature: 'Best For', retail: 'Personal users, long-term investments', oem: 'System builders, custom PCs, budget builds' },
                ].map((row) => (
                  <TableRow key={row.feature}>
                    <TableCell sx={{ fontWeight: 600 }}>{row.feature}</TableCell>
                    <TableCell align="center" sx={{ color: 'primary.main', fontWeight: 600 }}>{row.retail}</TableCell>
                    <TableCell align="center" sx={{ opacity: 0.8 }}>{row.oem}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Verified Reviews Section Enhancement */}
        <Box sx={{ mt: 15 }}>
          <Grid container spacing={8}>
            {/* Sidebar: Overall Rating */}
            <Grid item xs={12} md={4}>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>Customer Reviews</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="h2" sx={{ fontWeight: 800 }}>{product.rating}</Typography>
                <Box>
                  <Rating value={product.rating} precision={0.1} readOnly sx={{ color: 'primary.main' }} />
                  <Typography variant="body2" color="text.secondary">Based on {product.reviewsCount} reviews</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Rating Bars */}
              {[5, 4, 3, 2, 1].map((star) => (
                <Box key={star} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <Typography variant="body2" sx={{ width: 60 }}>{star} Stars</Typography>
                  <Box sx={{ flexGrow: 1, height: 6, bgcolor: '#EEE', borderRadius: 3 }}>
                    <Box sx={{ width: star > 3 ? '92%' : '8%', height: '100%', bgcolor: 'primary.main', borderRadius: 3 }} />
                  </Box>
                  <Typography variant="body2" sx={{ width: 40, textAlign: 'right' }}>{star > 3 ? '92%' : '8'}%</Typography>
                </Box>
              ))}
            </Grid>

            {/* Main: Review List */}
            <Grid item xs={12} md={8}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 4 }}>Featured Reviews</Typography>

              {[
                { name: "Priya S.", rating: 5, date: "April 15, 2026", comment: "The activation key for Windows 11 was sent instantly. Followed the guide and it worked in under 2 minutes. Incredibly cheap and original!", location: "Bengaluru" },
                { name: "Rahul M.", rating: 5, date: "March 28, 2026", comment: "Was skeptical initially but the MS Office 2024 Home & Business key binded to my official Microsoft account perfectly. Solid services.", location: "Mumbai" },
                { name: "Ananya K.", rating: 5, date: "March 10, 2026", comment: "Purchased a Windows Server 2025 Standard key for our development environment. Activation was flawless and pricing is excellent.", location: "Hyderabad" }
              ].map((rev, i) => (
                <Box key={i} sx={{ mb: 6 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 45,
                          height: 45,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700
                        }}
                      >
                        {rev.name[0]}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700 }}>{rev.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckCircle sx={{ color: 'primary.main', fontSize: 14 }} />
                          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>Verified Purchaser</Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">{rev.date}</Typography>
                  </Box>
                  <Rating value={rev.rating} size="small" readOnly sx={{ mb: 1 }} />
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    "{rev.comment}"
                  </Typography>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', fontWeight: 600 }}>Purchased in {rev.location}</Typography>
                  <Divider sx={{ mt: 4 }} />
                </Box>
              ))}

              <Button variant="outlined" sx={{ mt: 2 }}>View All {product.reviewsCount} Reviews</Button>
            </Grid>
          </Grid>
        </Box>

        {/* Related Products Section */}
        <Box sx={{ mt: 15, mb: 10 }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 800, mb: 6 }}>
            You May Also Like
          </Typography>
          <Grid container spacing={3}>
            {localProducts
              .filter(p => p.slug !== slug)
              .slice(0, 4)
              .map(relatedProduct => (
                <Grid item xs={12} sm={6} md={3} key={relatedProduct.id}>
                  <ProductCard product={relatedProduct} />
                </Grid>
            ))}
          </Grid>
        </Box>

        {/* Recently Viewed */}
        {recentlyViewed.length > 0 && (
          <Box sx={{ mt: 10, mb: 10 }}>
            <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 800, mb: 6 }}>
              Recently Viewed
            </Typography>
            <Grid container spacing={3}>
              {recentlyViewed.map(p => (
                <Grid item xs={12} sm={6} md={3} key={p.id}>
                  <ProductCard product={p} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ProductDetail;
